import { sortLensesByDisplayOrder } from './scenarioLensOrder.helpers';

import type { LensCatalogData } from './scenarioLensCatalog.types';
import type { ScenarioViewLens, ViewDirtyStateParams } from './scenarioLens.types';
import {
    areLensMappingsEqual,
    computeSelectionDirtyLensIds,
} from './viewSavePayload.helpers';

const serializeLensSettingsState = (lenses: ScenarioViewLens[]): string =>
    sortLensesByDisplayOrder(lenses)
        .map((lens: ScenarioViewLens) => `${lens.lens_id}:${lens.is_selected ? 1 : 0}:${lens.display_order}`)
        .join('|');

export const getActiveLensIdFromLenses = (lenses: ScenarioViewLens[]): number | null =>
    lenses.find((lens) => lens.is_active_lens)?.lens_id ?? null;

export const hasActiveLensChanged = (
    baselineLenses: ScenarioViewLens[],
    nextLenses: ScenarioViewLens[],
): boolean =>
    getActiveLensIdFromLenses(baselineLenses) !== getActiveLensIdFromLenses(nextLenses);

export const VIEW_DIRTY_REASON_LABELS = {
    lens_mappings: 'Lens tab selection or display order changed',
    catalog_column_visibility: 'Column visibility edited (catalog draft)',
    settings_panel: 'Lens settings panel draft changed',
    custom_lens_added: 'Custom lens added locally',
} as const;

export type ViewDirtyReason = keyof typeof VIEW_DIRTY_REASON_LABELS;

export type ViewDirtyDiagnostics = {
    isDirty: boolean;
    reasons: ViewDirtyReason[];
    details: {
        baselineActiveLensId: number | null;
        currentActiveLensId: number | null;
        dirtyCatalogLensIds: number[];
        lensMappingsChanged: boolean;
        settingsPanelChanged: boolean;
    };
};

/** True when lens list drafts (order/selection) differ from the persisted view. */
export const hasLensListDraftChanges = (
    viewLenses: ScenarioViewLens[],
    draftLenses: ScenarioViewLens[],
): boolean => serializeLensSettingsState(viewLenses) !== serializeLensSettingsState(draftLenses);

/** True when lens settings panel drafts differ from the persisted view (order, selection, or catalog). */
export const hasSettingsPanelDraftChanges = (
    viewLenses: ScenarioViewLens[],
    draftLenses: ScenarioViewLens[],
    catalogDraftsByLensId: Record<number, LensCatalogData>,
): boolean => {
    if (Object.keys(catalogDraftsByLensId).length > 0) {
        return true;
    }

    return hasLensListDraftChanges(viewLenses, draftLenses);
};

export const diagnoseViewDirtyState = (params: ViewDirtyStateParams): ViewDirtyDiagnostics => {
    const baselineActiveLensId = getActiveLensIdFromLenses(params.baselineLenses);
    const currentActiveLensId = getActiveLensIdFromLenses(params.viewLenses);
    const lensMappingsChanged = !areLensMappingsEqual(params.baselineLenses, params.viewLenses);
    const dirtyCatalogLensIds = computeSelectionDirtyLensIds(
        params.catalogDraftsByLensId,
        params.resolveBaselineCatalog,
    );
    const settingsPanelChanged = hasSettingsPanelDraftChanges(
        params.viewLenses,
        params.draftLenses,
        params.catalogDraftsByLensId,
    );

    const reasons: ViewDirtyReason[] = [];
    if (lensMappingsChanged) {
        reasons.push('lens_mappings');
    }
    if (dirtyCatalogLensIds.length > 0) {
        reasons.push('catalog_column_visibility');
    }
    if (settingsPanelChanged) {
        reasons.push('settings_panel');
    }

    return {
        isDirty: reasons.length > 0,
        reasons,
        details: {
            baselineActiveLensId,
            currentActiveLensId,
            dirtyCatalogLensIds,
            lensMappingsChanged,
            settingsPanelChanged,
        },
    };
};

/** True when local view state differs from the baseline captured at load/last save. */
export const computeViewHasLocalEdits = (params: ViewDirtyStateParams): boolean =>
    diagnoseViewDirtyState(params).isDirty;
